import { useRef, useEffect, useState } from 'react';
import { B } from '../../constants/brand';
import { Btn, Icon } from '../ui';

// ─── Pencil/stylus handwriting canvas ────────────────────────────────────────
// Pointer-events based, so it works with Apple Pencil / any stylus, mouse, or
// finger. Exports the ink as a white-background JPEG that can either be run
// through the OCR/Claude pipeline (Convert) or attached as a drawing (Keep).

const PEN_COLOR  = '#1a2a32';
const HEIGHT_SM  = 300;
const UNDO_MAX   = 12;

// Larger writing area for tablets — capped so it stays on-screen.
const expandedHeight = () =>
  Math.max(HEIGHT_SM, Math.min(700, Math.round((window.innerHeight || 800) * 0.7)));

// Full-screen writing area — fills the viewport minus room for the toolbars.
const fullscreenHeight = () => Math.max(400, (window.innerHeight || 800) - 150);

export const InkCanvas = ({ onConvert, onKeepDrawing, onClose, busy }) => {
  const canvasRef     = useRef(null);
  const ctxRef        = useRef(null);
  const dims          = useRef({ w: 0, h: HEIGHT_SM });
  const drawing       = useRef(false);
  const activePointer = useRef(null);
  const penSeen       = useRef(false);     // once a pen is used, reject touch (palm rejection)
  const last          = useRef({ x: 0, y: 0 });
  const undoStack     = useRef([]);
  const [hasInk, setHasInk]     = useState(false);
  const [canvasH, setCanvasH]   = useState(HEIGHT_SM);
  const [fullscreen, setFullscreen] = useState(false);

  // (Re)initialise the backing store whenever the height changes, preserving
  // any existing ink. Width is constant (container width) so redrawing the old
  // bitmap at its natural size adds/removes space at the bottom without distortion.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cssW = canvas.clientWidth || 600;
    const cssH = canvasH;
    const dpr  = window.devicePixelRatio || 1;
    const prevUrl = canvas.width ? canvas.toDataURL() : null;
    const prevH   = dims.current.h;

    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    dims.current  = { w: cssW, h: cssH };

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = PEN_COLOR;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssW, cssH);
    ctxRef.current = ctx;

    // ImageData snapshots are tied to the old pixel size — reset undo on resize.
    undoStack.current = [];

    if (prevUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssW, prevH);
      img.src = prevUrl;
    }
  }, [canvasH]);

  // Keep the full-screen canvas filling the viewport as it resizes / rotates.
  useEffect(() => {
    if (!fullscreen) return;
    const onResize = () => setCanvasH(fullscreenHeight());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fullscreen]);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const lineWidthFor = (e) =>
    (e.pointerType === 'pen' && e.pressure > 0) ? 1 + e.pressure * 3.5 : 2.2;

  const snapshot = () => {
    const ctx = ctxRef.current, canvas = canvasRef.current;
    try {
      undoStack.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
      if (undoStack.current.length > UNDO_MAX) undoStack.current.shift();
    } catch { /* tainted canvas — skip undo history */ }
  };

  const strokeTo = (p, e) => {
    const ctx = ctxRef.current;
    ctx.lineWidth = lineWidthFor(e);
    const midX = (last.current.x + p.x) / 2;
    const midY = (last.current.y + p.y) / 2;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.quadraticCurveTo(last.current.x, last.current.y, midX, midY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const onPointerDown = (e) => {
    if (busy) return;
    if (e.pointerType === 'pen') penSeen.current = true;
    if (penSeen.current && e.pointerType === 'touch') return; // palm rejection
    e.preventDefault();
    snapshot();
    drawing.current = true;
    activePointer.current = e.pointerId;
    last.current = pointFromEvent(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!drawing.current || e.pointerId !== activePointer.current) return;
    e.preventDefault();
    // Draw every coalesced sample for smooth, high-frequency strokes (Pencil).
    const samples = e.getCoalescedEvents ? e.getCoalescedEvents() : [];
    if (samples.length) {
      for (const s of samples) strokeTo(pointFromEvent(s), s);
    } else {
      strokeTo(pointFromEvent(e), e);
    }
    if (!hasInk) setHasInk(true);
  };

  const onPointerUp = (e) => {
    if (e.pointerId !== activePointer.current) return;
    drawing.current = false;
    activePointer.current = null;
  };

  const clearAll = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    snapshot();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, dims.current.w, dims.current.h);
    setHasInk(false);
  };

  const undo = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    ctxRef.current.putImageData(prev, 0, 0); // device-pixel restore, ignores transform
    // Snapshots are taken pre-stroke; an empty stack means we're back to blank.
    setHasInk(undoStack.current.length > 0);
  };

  const exportJpeg = () => {
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.92);
    const base64 = dataUrl.split(',')[1] || '';
    return { base64, sizeBytes: Math.round(base64.length * 0.75) };
  };

  const handleConvert = () => { if (hasInk && !busy) onConvert(exportJpeg()); };
  const handleKeep    = () => { if (hasInk && !busy) onKeepDrawing(exportJpeg()); };

  const expanded = !fullscreen && canvasH > HEIGHT_SM;

  const toggleExpand = () => setCanvasH(expanded ? HEIGHT_SM : expandedHeight());
  const toggleFullscreen = () => {
    if (fullscreen) { setFullscreen(false); setCanvasH(HEIGHT_SM); }
    else { setFullscreen(true); setCanvasH(fullscreenHeight()); }
  };

  const containerStyle = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 5000, background: B.surfaceMuted, padding: '12px 16px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }
    : { borderTop: `1px solid ${B.border}`, padding: '10px 12px', background: B.surfaceMuted };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: B.textLt }}>
          Write below with a pencil or stylus, then convert to text or keep the drawing.
        </div>
        <div style={{ flex: 1 }} />
        {!fullscreen && (
          <Btn variant="ghost" size="sm" disabled={busy} onClick={toggleExpand}>
            <Icon name={expanded ? 'minimize' : 'maximize'} size={14} /> {expanded ? 'Collapse' : 'Expand'}
          </Btn>
        )}
        <Btn variant="ghost" size="sm" disabled={busy} onClick={toggleFullscreen}>
          <Icon name={fullscreen ? 'fullscreenExit' : 'fullscreen'} size={14} /> {fullscreen ? 'Exit full screen' : 'Full screen'}
        </Btn>
      </div>
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          width: '100%', height: canvasH, display: 'block',
          background: B.white, border: `1px solid ${B.border}`, borderRadius: 6,
          touchAction: 'none', cursor: 'crosshair',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <Btn variant="ghost" size="sm" disabled={busy} onClick={undo}><Icon name="undo" size={14} /> Undo</Btn>
        <Btn variant="ghost" size="sm" disabled={busy || !hasInk} onClick={clearAll}>Clear</Btn>
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" disabled={busy} onClick={onClose}>Close</Btn>
        <Btn variant="ghost" size="sm" disabled={busy || !hasInk} onClick={handleKeep}>Keep drawing</Btn>
        <Btn size="sm" disabled={busy || !hasInk} onClick={handleConvert}>
          {busy ? 'Converting…' : 'Convert to text'}
        </Btn>
      </div>
    </div>
  );
};
